export interface CaseStudy {
  id: string;
  slug: string;
  title: string;
  category: string;
  overview: string;
  challengePrefix: string;
  challenge: string[];
  solution: string;
  solutionList: string[];
  techStack: string[];
  outcomes: string[];
}

export const caseStudies: CaseStudy[] = [
  {
    id: "01",
    slug: "pathpulse",
    title: "PATHPULSE",
    category: "Public Sector · Payments Infrastructure",
    overview: "PathPulse is payment and settlement infrastructure built for a national government. Dotslashlab engineered dual-chain rails on Stellar and Cardano to move and settle government payments quickly, cheaply, and with a tamper-proof, auditable record across institutions.",
    challengePrefix: "Government payment rails are slow, costly, and hard to audit:",
    challenge: ["Slow settlement across institutions", "High transaction & reconciliation costs", "Limited transparency and auditability", "Fragmented legacy payment systems", "Inter-agency and cross-border complexity"],
    solution: "Dotslashlab designed and built dual-chain settlement infrastructure for government-scale disbursement:",
    solutionList: ["Stellar-based settlement rails", "Cardano integration for on-chain assurance", "Automated reconciliation", "Audit-ready transaction ledger", "Secure institutional onboarding", "Scalable disbursement workflows"],
    techStack: ["Stellar", "Cardano", "Rust", "TypeScript", "Node.js", "PostgreSQL", "Cloud Infrastructure"],
    outcomes: ["Dual-chain rails for government payments", "Faster, lower-cost settlement", "Transparent, auditable transactions", "Built for national scale"]
  },
  {
    id: "02",
    slug: "adcotel",
    title: "ADCOTEL",
    category: "Travel Tech · Booking Platform",
    overview: "Adcotel is a full booking platform — mobile app, web app, and the complete infrastructure behind them. Dotslashlab is rebuilding the product end-to-end on a modern, type-safe stack, including host and admin financial tooling with compliant invoicing.",
    challengePrefix: "A growing booking business had outgrown its original build:",
    challenge: ["Aging, hard-to-scale infrastructure", "Separate mobile and web experiences to unify", "Complex host & admin financial reporting", "Need for compliant invoicing (GST / TDS)", "Reliable payments and settlements"],
    solution: "Dotslashlab is rebuilding Adcotel as one modern, scalable system from app to infrastructure:",
    solutionList: ["React Native mobile app", "Full-featured web application", "Type-safe backend (Hono + Drizzle)", "Supabase data & auth layer", "Host & superadmin earnings panels", "Invoicing with GST / TDS and xlsx / pdf / csv exports"],
    techStack: ["React Native", "Next.js", "TypeScript", "Hono", "Drizzle ORM", "Supabase", "PostgreSQL"],
    outcomes: ["Unified mobile + web experience", "Modern, scalable infrastructure", "Host & admin finance tooling", "Compliant invoicing & exports"]
  },
  {
    id: "03",
    slug: "maharashtra-govt",
    title: "MAHARASHTRA GOVT",
    category: "Public Sector · Government",
    overview: "Dotslashlab is delivering software projects with government departments in Maharashtra, India — designing secure, reliable digital infrastructure for public-sector use, from internal workflows to public-facing services.",
    challengePrefix: "Public-sector software has to be secure, reliable, and accountable:",
    challenge: ["Strict security & compliance requirements", "Legacy systems and manual processes", "Scale and reliability for public use", "Transparency and auditability"],
    solution: "Dotslashlab partners with government departments to design and ship dependable systems:",
    solutionList: ["Secure, compliant architecture", "Scalable public-facing services", "Workflow digitization", "Auditable data and reporting"],
    techStack: ["Next.js", "TypeScript", "Node.js", "PostgreSQL", "Cloud Infrastructure"],
    outcomes: ["Digital infrastructure for government departments", "Secure and compliant delivery", "Built for public-sector scale", "Ongoing partnership"]
  },
  {
    id: "04",
    slug: "chatlio",
    title: "CHATLIO",
    category: "AI SaaS Platform",
    overview: "Chatlio is an AI-powered communication platform designed to help businesses centralize conversations, automate support workflows, and improve customer engagement. Built on Matrix infrastructure, Chatlio provides organizations with complete ownership of communication data while enabling intelligent automation across customer interactions.",
    challengePrefix: "Most businesses rely on fragmented communication channels. This creates:",
    challenge: ["Slow response times", "Lost customer conversations", "Poor collaboration", "Lack of automation", "Limited data ownership"],
    solution: "Dotslashlab designed and developed Chatlio as a modern communication layer powered by AI and Matrix infrastructure. Key capabilities include:",
    solutionList: ["Real-time messaging", "Team collaboration", "AI-powered assistance", "Customer support workflows", "Lead qualification systems", "Scalable communication architecture"],
    techStack: ["Next.js", "TypeScript", "Matrix Protocol", "Node.js", "PostgreSQL", "Docker", "GCP", "AI Integrations"],
    outcomes: ["Centralized communication management", "Reduced manual support effort", "Improved team collaboration", "Enhanced customer engagement"]
  },
  {
    id: "05",
    slug: "boardlyx",
    title: "BOARDLYX",
    category: "AI + Web3 Productivity SaaS",
    overview: "BoardlyX is an AI-powered team productivity platform that unifies Kanban project management, real-time team chat, and analytics into a single workspace, with Web3 wallet authentication and on-chain task verification. It is built for modern, distributed teams that want collaboration, accountability, and a tamper-proof record of completed work.",
    challengePrefix: "Distributed teams stitch together separate tools to get work done, and lose accountability in the gaps:",
    challenge: ["Work scattered across boards, chat & docs", "No verifiable record of completed work", "Fragmented, slow team communication", "Limited visibility into productivity", "Notifications buried across apps"],
    solution: "Dotslashlab designed and built BoardlyX as a single real-time workspace that combines project management, collaboration, and on-chain proof of work:",
    solutionList: ["Drag-and-drop Kanban boards", "Teams with email invites & shared workspaces", "Real-time group chat & DMs with media sharing", "Analytics dashboards & productivity insights", "On-chain task logging via smart contract", "Wallet authentication with RainbowKit", "AI assistance powered by Google Gemini", "Telegram bot notifications"],
    techStack: ["React 19", "TypeScript", "Vite", "Zustand", "Tailwind CSS", "Recharts", "Socket.io", "Express.js", "PostgreSQL", "wagmi / viem", "Solidity (Sepolia)", "Google Gemini", "Telegram Bot API"],
    outcomes: ["Unified work, chat & analytics in one workspace", "Tamper-proof on-chain task records", "Real-time team collaboration", "AI-assisted, wallet-native experience"]
  },
  {
    id: "06",
    slug: "easyhyr",
    title: "EASYHYR",
    category: "AI Job-Matching Platform",
    overview: "EasyHyr is an AI-powered job-matching and application-optimization platform. It continuously ingests live job listings, matches them to a candidate's experience using semantic vector search, and uses LLMs to tailor resumes, surface skill gaps, and validate ATS compatibility, turning a slow, manual job hunt into an automated, data-driven pipeline.",
    challengePrefix: "Job seeking is repetitive, opaque, and hard to optimize:",
    challenge: ["Manually searching across multiple job boards", "Guessing which roles actually fit", "Re-tailoring a resume for every application", "No visibility into why ATS systems reject", "Identifying missing skills & keywords"],
    solution: "Dotslashlab engineered EasyHyr as an end-to-end matching and optimization pipeline:",
    solutionList: ["Automated job scraping (LinkedIn, Naukri, Indeed)", "Semantic matching via vector embeddings", "Persona-based resume ranking", "AI gap analysis for missing skills & keywords", "LLM-powered LaTeX resume rewrites", "ATS scoring & validation before applying", "Live LaTeX resume editor in-browser"],
    techStack: ["Next.js 14", "TypeScript", "Tailwind / shadcn", "Fastify", "Prisma", "PostgreSQL", "Redis / BullMQ", "Qdrant", "BGE-M3 Embeddings", "Anthropic", "Playwright", "AWS S3 / CloudFront", "Monaco Editor"],
    outcomes: ["Automated job discovery & matching", "Higher-relevance role recommendations", "Faster, tailored applications", "Improved ATS pass-through", "Data-driven resume optimization"]
  },
  {
    id: "07",
    slug: "yap-market",
    title: "YAP.MARKET",
    category: "AI Agents & Growth Platform",
    overview: "Yap.market is a growth-focused platform designed around referral mechanics, community engagement, and user acquisition systems. The platform combines automation, incentives, and engagement loops to drive sustainable growth.",
    challengePrefix: "Early-stage products struggle with:",
    challenge: ["User acquisition", "Retention", "Community growth", "Referral tracking"],
    solution: "Dotslashlab developed a scalable platform featuring:",
    solutionList: ["Referral systems", "Growth loops", "Community incentives", "Automated tracking", "Analytics infrastructure"],
    techStack: ["Next.js", "TypeScript", "PostgreSQL", "API Integrations", "Cloud Infrastructure"],
    outcomes: ["Successfully scaled user acquisition", "Created measurable referral loops", "Improved retention mechanics", "Enabled growth experimentation"]
  },
  {
    id: "08",
    slug: "xade-finance",
    title: "XADE FINANCE",
    category: "AI Trading Infrastructure",
    overview: "Trade.Xade.Finance is a modern trading platform combining AI agents, trading infrastructure, and cross-market accessibility. The platform enables users to interact with financial markets through intelligent workflows and automated execution systems.",
    challengePrefix: "Modern traders face:",
    challenge: ["Fragmented trading tools", "Complex interfaces", "Cross-platform limitations", "Limited automation capabilities"],
    solution: "Dotslashlab contributed to building:",
    solutionList: ["Trading dashboards", "User experience workflows", "Real-time interfaces", "Trading infrastructure components", "AI-enabled interactions"],
    techStack: ["React", "Next.js", "TypeScript", "Web3 Integrations", "Trading APIs", "Cloud Infrastructure"],
    outcomes: ["Improved trading accessibility", "Enhanced platform usability", "Streamlined user workflows"]
  },
  {
    id: "09",
    slug: "gitcode-v3",
    title: "GITCODE V3",
    category: "Developer Platform",
    overview: "GitCode V3 is a developer-focused platform designed to improve collaboration, productivity, and project management for engineering teams.",
    challengePrefix: "Developers often struggle with fragmented workflows across multiple platforms.",
    challenge: [],
    solution: "The platform introduced:",
    solutionList: ["Unified collaboration", "Project management workflows", "Developer-centric tooling", "Scalable architecture"],
    techStack: ["Next.js", "TypeScript", "Cloud Infrastructure", "Git Integrations"],
    outcomes: ["Improved developer workflows", "Streamlined collaboration", "Faster project organization"]
  },
  {
    id: "10",
    slug: "astra-finance",
    title: "ASTRA FINANCE",
    category: "DeFi Infrastructure",
    overview: "Astra Finance represents secure and scalable DeFi infrastructure focused on user experience and protocol reliability.",
    challengePrefix: "DeFi products often struggle with usability and security simultaneously.",
    challenge: [],
    solution: "Dotslashlab contributed to:",
    solutionList: ["Frontend architecture", "Protocol integrations", "UX improvements", "Scalability enhancements"],
    techStack: ["Starknet", "React", "TypeScript", "Smart Contracts"],
    outcomes: ["Enhanced user experience", "Improved protocol accessibility", "Secure infrastructure deployment"]
  },
  {
    id: "11",
    slug: "echo-ai",
    title: "ECHO AI",
    category: "Enterprise AI",
    overview: "Echo AI is an enterprise-focused automation and workflow orchestration platform leveraging large language models.",
    challengePrefix: "Organizations needed AI systems that integrate with existing workflows.",
    challenge: [],
    solution: "Dotslashlab developed:",
    solutionList: ["AI orchestration systems", "Workflow automation", "Specialized LLM integrations", "Enterprise-ready architecture"],
    techStack: ["OpenAI", "Node.js", "Next.js", "Vector Databases", "Cloud Infrastructure"],
    outcomes: ["Operational efficiency improvements", "Reduced repetitive work", "Faster decision support"]
  },
  {
    id: "12",
    slug: "pp-editor",
    title: "PP EDITOR",
    category: "AI Media Platform",
    overview: "PP Editor is an AI-powered content production platform designed to simplify video editing workflows.",
    challengePrefix: "Content teams spend significant time editing and preparing media.",
    challenge: [],
    solution: "The platform provides:",
    solutionList: ["AI editing assistance", "Automated caption generation", "Workflow automation", "Faster publishing pipelines"],
    techStack: ["Next.js", "AI APIs", "Media Processing Services", "Cloud Infrastructure"],
    outcomes: ["Faster content production", "Reduced manual editing effort", "Improved publishing efficiency"]
  }
];
